import axios from 'axios';
import DropZone from 'components/dropzone/DropZone';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { PxDropdownSc } from 'components/final-form/Select';
import EmailCampaignsView from 'components/guarantors/emailcampaign/EmailCampaignsView';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import PatientEmailImportTable from 'components/guarantors/patient-emails/PatientEmailImportTable';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import LoaderComponent from 'components/LoaderComponent';
import { FormApi } from 'final-form';
import React, { useEffect, useState } from 'react';
import { FileError, FileRejection } from 'react-dropzone';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Header, Loader, Modal } from 'semantic-ui-react';
import { sendBroadcastEmails } from 'services/broadcastEmailServices';
import { uploadPatientEmailFile } from 'services/patientEmailImportServices';
import styled from 'styled-components';
import { EmailCampaignDto, PatientEmailImportDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { getActiveEmailCampaigns } from 'services/emailCampaignServices';

const BroadcastEmailContainer = styled(PraxStyledSegment)`
  height: 100%;

  .side-button {
    margin-left: 1rem;
  }
`;

const StyledGridColumn = styled(Grid.Column)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`;

const SearchFormDiv = styled.div`

  .actions-row {

    display: flex;
    flex-direction: row;
    align-items: baseline;

    button {
      margin-right: 1rem;
    }

    .submit-message {
      display: flex;
      flex-direction: row;
      align-self: center;

      .ui.inline.loader.active,
      .ui.inline.loader.visible {
        margin: 0 0.9rem;
      }
    }
  }
`;

interface Props extends MessageSourceProps, GuarantorsConsumerRenderProps {

}

const PatientEmailImportView = (props: Props) => {

  const [foundPatients, setFoundPatients] =
      useState([] as PatientEmailImportDto[]);

  const [patientEmailLoaded, setPatientEmailLoaded] =
      useState(true);

  const [errorMessage, setErrorMessage] =
      useState(undefined as string | undefined);

  const [emailSending, setEmailSending] =
      useState(false);

  const [emailCampaignId, setEmailCampaignId] =
      useState(undefined as number | undefined);
  const [emailCampaigns, setEmailCampaigns] =
      useState([] as EmailCampaignDto[]);
  const [emailCampaignOptions, setEmailCampaignOptions] =
      useState([] as DropdownOption[]);
  const [editCampaignsModalOpen, setEditCampaignsModalOpen] =
      useState(false);

  const cancelTokenSource = axios.CancelToken.source();

  const {getMessage} = props;

  useEffect(() => {
    fetchRequiredData();
  }, []);

  const fetchRequiredData = async () => {

    const emailCampaigns = await getActiveEmailCampaigns(cancelTokenSource);
    setEmailCampaigns(emailCampaigns);
    setEmailCampaignOptions(mapToDropdownOptionArray(emailCampaigns, "name", "id", "id"));
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.invalidDocumentInput,
      errorUtils.unknownEmailCampaign,
      errorUtils.notExistingEmailCampaignTemplate
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const onDropAccepted = (acceptedFiles: File[], form: FormApi) => {

    const onFinally = () => {
      setPatientEmailLoaded(true);
    };

    form.change("file", acceptedFiles[0]);
    const file = form.getState().values.file;

    setPatientEmailLoaded(false);

    uploadPatientEmailFile(file, cancelTokenSource)
        .then(response => {
              setFoundPatients(response);
            }
        )
        .catch((e) => handleError(e.response.data))
        .finally(onFinally);
  };

  const onDropRejected = (rejections: FileRejection[]) => {

    let errors: FileError[] = [];
    rejections.forEach((rejection) => {
      errors = rejection.errors;
    })

    errors.forEach((error) => {
      setErrorMessage(error.message);
    });
  };

  const resetImport = (form: FormApi) => {
    form.change("file", undefined);
    setFoundPatients([]);
    setEmailCampaignId(undefined);
  }

  const sendEmail = async () => {
    const selectedEmails = foundPatients.filter(
        pat => pat.selected && !pat.invalid);
    let createdRun;
    setEmailSending(true);

    const campaignId = emailCampaignId ? emailCampaignId : -1;

    try {
      createdRun = await sendBroadcastEmails(selectedEmails, campaignId, cancelTokenSource);
    } catch (e) {
      handleError(e.response.data);
      return;
    } finally {
      setEmailSending(false);
    }
  }

  const openEditCampaigns = () => {
    setEditCampaignsModalOpen(true)
  }

  const closeEditCampaigns = () => {
    setEditCampaignsModalOpen(false)
  }

  const renderImportSegmentForm = ({values, form}: FormRenderProps): React.ReactNode => {

    const uploadMessage = values.file ? values.file.name : getMessage("patientEmail.upload");

    return (
        <PxGrid columns={1}>
          {errorMessage
              && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                    </Grid.Column>
                  </Grid.Row>
              )
          }
          <Grid.Row>
            <Grid.Column>
              <Header>{getMessage("patientEmail.import")}</Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <StyledGridColumn width={3}>
              <PxGrid>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <Header size="tiny">{getMessage("patientEmail.emailCampaign.label")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <PxDropdownSc
                        selection
                        value={emailCampaignId}
                        onChange={(e: any, {value}: any) => {
                          setEmailCampaignId(value)
                        }}
                        options={emailCampaignOptions}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="actions-row">
                      <Button
                          primary
                          compact
                          type="button"
                          onClick={() => openEditCampaigns()}
                      >
                        {getMessage("patientEmail.emailCampaign.button")}
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </StyledGridColumn>
            <StyledGridColumn width={5}>
              <PxGrid>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <Header size="tiny">{getMessage("patientEmail.upload")}</Header>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="middle">
                    <DropZone
                        multiple={false}
                        message={uploadMessage}
                        onDropAccepted={(acceptedFiles: File[]) => onDropAccepted(acceptedFiles, form)}
                        onDropRejected={onDropRejected}
                        disabled={!emailCampaignId}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div className="actions-row">
                      <Button
                          primary
                          compact
                          type="button"
                          onClick={() => resetImport(form)}
                          disabled={foundPatients.length == 0 || !emailCampaignId}
                      >
                        {getMessage("button.reset")}
                      </Button>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </StyledGridColumn>
          </Grid.Row>
        </PxGrid>
    )
  }

  const renderPatientsImportSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true, values: true}}
            render={renderImportSegmentForm}
        />
    );
  };

  const renderUpsertCampaignsModal = (): JSX.Element => {

    return (
        <Modal
            open={editCampaignsModalOpen}
            onClose={() => closeEditCampaigns()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("emailCampaigns.header")}</Modal.Header>
          <FormModalContentContainer>
            <EmailCampaignsView
                onCancel={closeEditCampaigns}
                emailCampaigns={emailCampaigns}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

  const onPatientSelect = (patients: PatientEmailImportDto[]) => {
    setFoundPatients(patients);
  }

  const countAllPatients = foundPatients.length;
  const countSelectedPatients = foundPatients.filter(patient => patient.selected).length;

  return (
      <BroadcastEmailContainer>
        <SearchFormDiv>
          {renderPatientsImportSegment()}
        </SearchFormDiv>

        <div>
          {getMessage("patientEmail.selectedParticipants")}:&nbsp;&nbsp;
          <strong>{countSelectedPatients}/{countAllPatients}</strong>
        </div>

        <StyledViewTableRow id="patientEmailImportTable">
          {
            !patientEmailLoaded
                ? <LoaderComponent message={getMessage("patientEmail.importingPatients.message")}/>
                : foundPatients && foundPatients.length > 0
                    ? <PatientEmailImportTable
                        foundPatients={foundPatients}
                        onPatientSelected={onPatientSelect}
                    />
                    : <></>
          }
        </StyledViewTableRow>
        <FormButtonsRow>
          <Button
              primary
              compact
              type="button"
              onClick={() => sendEmail()}
              disabled={countSelectedPatients == 0 || emailSending || !emailCampaignId}
          >
            {getMessage("patientEmail.sendEmailToSelectedPatents.button")}
          </Button>
          {
              emailSending &&
              <>
                <Loader inline active size="small"/>
                <>{getMessage("patientEmail.sendingEmail.loadMessage")}</>
              </>
          }
        </FormButtonsRow>

        {renderUpsertCampaignsModal()}

      </BroadcastEmailContainer>
  );
}

export default withMessages(withGuarantorsContext(PatientEmailImportView));
